import { GoodRoad } from '@/components/roadmap/data-model'
import { parseRoadMap } from '@/components/roadmap/utils'
import { getGoodRoadName } from '@/utils/constants'
import { Table } from 'vue'

export interface GoodRoadTableEntry {
    table: Table
    goodRoads: GoodRoad[]
    goodRoadName: string
}

export function getTableGoodRoads(table: Table): GoodRoad[] {
    const resultList = table.expand?.resultList?.join('#') || ''
    return parseRoadMap(resultList).goodRoads
}

export function formatGoodRoadName(id: number, gameTypeNo: string): string {
    return getGoodRoadName(id, gameTypeNo)
}

export function buildGoodRoadEntry(table: Table): GoodRoadTableEntry | null {
    const goodRoads = getTableGoodRoads(table)
    if (!goodRoads.length) return null

    return buildTableEntry(table, goodRoads)
}

export function buildTableEntry(
    table: Table,
    goodRoads: GoodRoad[] = getTableGoodRoads(table)
): GoodRoadTableEntry {
    return {
        table,
        goodRoads,
        goodRoadName: goodRoads.length
            ? formatGoodRoadName(goodRoads[0].id, table.gameTypeNo)
            : ''
    }
}

export function filterGoodRoadTables(tables: Table[]): GoodRoadTableEntry[] {
    return tables
        .map(buildGoodRoadEntry)
        .filter((entry): entry is GoodRoadTableEntry => entry !== null)
}

export function buildPanelListData(tables: Table[]): GoodRoadTableEntry[] {
    return tables
        .map(table => buildTableEntry(table))
        .sort((a, b) => {
            const aIsGoodRoad = a.goodRoads.length > 0 ? 1 : 0
            const bIsGoodRoad = b.goodRoads.length > 0 ? 1 : 0
            if (aIsGoodRoad !== bIsGoodRoad) return bIsGoodRoad - aIsGoodRoad

            if (aIsGoodRoad && bIsGoodRoad) {
                return b.table.timeStamp - a.table.timeStamp || a.table.id - b.table.id
            }

            return a.table.id - b.table.id
        })
}

export const GOOD_ROAD_GAME_TYPES = ['G002', 'G003'] as const

export function isGoodRoadGameType(gameTypeNo: string): boolean {
    return gameTypeNo === 'G002' || gameTypeNo === 'G003'
}

export function filterGoodRoadGameTables(tables: Table[]): Table[] {
    return tables.filter(table => isGoodRoadGameType(table.gameTypeNo))
}

export function splitGoodRoadTablesByType(
    tables: Table[]
): Record<string, Table[]> {
    return {
        G002: tables.filter(table => table.gameTypeNo === 'G002'),
        G003: tables.filter(table => table.gameTypeNo === 'G003')
    }
}

export function countGoodRoadTables(tables: Table[]): number {
    return filterGoodRoadTables(tables).length
}

export function getMemberSideBetAmounts(
    table: Table,
    memberId: number
): { player: number; banker: number } {
    let player = 0
    let banker = 0
    const list = table.expand?.betFormList || []

    list.forEach(betInfo => {
        if (betInfo.memberId !== memberId) return

        const { playName, amount } = betInfo
        if (playName === '庄' || playName === '龙' || playName === '免佣庄') {
            banker += amount
        } else if (playName === '闲' || playName === '虎') {
            player += amount
        }
    })

    return { player, banker }
}

export function mergeTableDetail(base: Table, detail: Table): Table {
    return {
        ...base,
        ...detail,
        expand: {
            ...base.expand,
            ...detail.expand
        }
    }
}

export function mergeTableBetUpdate(base: Table, update: Table): Table {
    const updateExpand = update.expand || base.expand
    const betFormList =
        updateExpand.betFormList !== undefined &&
        updateExpand.betFormList !== null
            ? updateExpand.betFormList
            : base.expand?.betFormList ?? []

    return {
        ...base,
        expand: {
            ...base.expand,
            betFormList,
            amount:
                updateExpand.amount !== undefined &&
                updateExpand.amount !== null
                    ? updateExpand.amount
                    : base.expand?.amount,
            xianAmount: updateExpand.xianAmount ?? base.expand?.xianAmount,
            zhuangAmount:
                updateExpand.zhuangAmount ?? base.expand?.zhuangAmount,
            xianDuiAmount:
                updateExpand.xianDuiAmount ?? base.expand?.xianDuiAmount,
            zhuangDuiAmount:
                updateExpand.zhuangDuiAmount ?? base.expand?.zhuangDuiAmount,
            heAmount: updateExpand.heAmount ?? base.expand?.heAmount
        }
    }
}

export function mergeMultiDetailIntoTables(
    hallTables: Table[],
    detailTables: Table[]
): Table[] {
    if (!detailTables.length) return hallTables

    const detailById = new Map(detailTables.map(table => [table.id, table]))

    return hallTables.map(table => {
        const detail = detailById.get(table.id)
        return detail ? mergeTableDetail(table, detail) : table
    })
}

export function collectGoodRoadTableIds(
    desksByType: Record<string, Table[]>
): number[] {
    const ids = new Set<number>()

    GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
        const tables = desksByType[gameTypeNo] || []
        tables.forEach(table => {
            ids.add(table.id)
        })
    })

    return Array.from(ids)
}
